import { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import ConsentFormSection from "@components/ConsentFormSection";
import DocumentPreview from "@components/drive/DocumentPreview";
import Documents from "@components/drive/Documents";
import styles from "@components/drive/Drive.module.css";
import DriveHeader from "@components/drive/DriveHeader";
import Worksheets from "@components/drive/Worksheets";
import FileResourceForm from "@components/forms/FileResourceForm";
import FolderForm from "@components/forms/FolderForm";
import Modal from "@components/Modal";
import LegalDocuments from "@components/trackers/LegalDocuments";
import Medications from "@components/trackers/Medications";
import Providers from "@components/trackers/Providers";
import Heading2 from "@components/typography/Heading2";
import useOpener from "@hooks/useOpener";
import useScrollPosition from "@hooks/useScrollPosition";
import { ScrollPositionPageTypeEnum } from "@typing/Enums";
import { FileResourceFragment, FolderFragment, GetJourneyWithDocumentsQuery } from "@typing/Generated";

type Journey = GetJourneyWithDocumentsQuery["journey"];

type Props = {
  journey: Journey;
};

const Drive = ({ journey }: Props) => {
  const pageRef = useRef<HTMLDivElement>(null);
  const consentFormRef = useRef<HTMLDivElement>(null);
  const fileResourcePreviewOpener = useOpener();

  const intl = useIntl();

  const [editingFileResource, setEditingFileResource] = useState<FileResourceFragment | null>(null);
  const [previewingFileResource, setPreviewingFileResource] = useState<FileResourceFragment | null>(null);
  const [editingFolder, setEditingFolder] = useState<FolderFragment | null>(null);

  useEffect(() => {
    let timeout: NodeJS.Timeout | null = null;

    if (location.hash.includes("consent-form")) {
      timeout = setTimeout(() => consentFormRef?.current?.scrollIntoView({ behavior: "smooth" }), 700);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  useScrollPosition(ScrollPositionPageTypeEnum.DOCUMENTS);

  const folderOpener = useOpener();
  const fileResourceOpener = useOpener();

  const handleFolderCreate = () => {
    setEditingFolder(null);
    folderOpener.open();
  };

  const handleFileResourceCreate = () => {
    setEditingFileResource(null);
    fileResourceOpener.open();
  };

  const handlePreviewFileResource = (fileResource: FileResourceFragment) => {
    setPreviewingFileResource(fileResource);
    fileResourcePreviewOpener.open();
  };

  return (
    <div ref={pageRef}>
      <DriveHeader
        handleFileResourceCreate={handleFileResourceCreate}
        handlePreviewFileResource={handlePreviewFileResource}
        journey={journey}
      />
      <Documents handleFolderCreate={handleFolderCreate} journey={journey} />
      <div className={styles.consentFormSection}>
        <Heading2>
          <FormattedMessage id="pages.drive.consentForm" />
        </Heading2>
        <ConsentFormSection journeyId={journey.id} />
      </div>
      <Worksheets journey={journey} />
      <div className={styles.trackers}>
        <Medications journey={journey} />
        <Providers journey={journey} />
        <LegalDocuments journey={journey} />
      </div>

      <DocumentPreview fileResource={previewingFileResource} journey={journey} opener={fileResourcePreviewOpener} />

      <Modal opener={fileResourceOpener} title={intl.formatMessage({ id: "pages.documents.uploadFile" })}>
        {({ modalDisplayed }) => (
          <FileResourceForm
            autofocus={modalDisplayed}
            fileResource={editingFileResource}
            journey={journey}
            onCancel={fileResourceOpener.close}
            onSuccess={fileResourceOpener.close}
          />
        )}
      </Modal>

      <Modal
        opener={folderOpener}
        title={intl.formatMessage({
          id: editingFolder ? "pages.documents.editFolder" : "pages.documents.createFolder"
        })}
      >
        {({ modalDisplayed }) => (
          <FolderForm
            autofocus={modalDisplayed}
            folder={editingFolder}
            journey={journey}
            onCancel={folderOpener.close}
            onSuccess={folderOpener.close}
          />
        )}
      </Modal>
    </div>
  );
};

export default Drive;
